<template>
  <div class="email_input__wrapper">
    <div
      class="email_input__item"
      v-for="(emailAddress, index) in inputValue"
      :key="index"
    >
      <TextInput
        v-model="inputValue[index]"
        validationType="email"
        inputType="email"
        :label="inputLabel"
        :required="true"
        :disabled="disabled"
        :enable-external-validation="enableExternalValidation"
        :external-validation-error="externalValidationErrors[index]"
        :external-validation-error-message="
          externalValidationErrorMessages[index]
        "
      />

      <InlineButton @click="remove(index)" :disabled="disabled" grey>
        <font-awesome-icon slot="icon" :icon="['far', 'times']" size="lg" />
      </InlineButton>
    </div>

    <div v-if="inputValue.length < maxSize">
      <button type="button" class="add_more" @click="add">
        <span class="delete_action">{{ addAnotherLabel }}</span>
        <font-awesome-icon :icon="['far', 'plus']" />
      </button>
    </div>
  </div>
</template>

<script>
import isMobile from "is-mobile";
import { faPlus, faTimes } from "@fortawesome/pro-regular-svg-icons";
import { library } from "@fortawesome/fontawesome-svg-core";
import { FontAwesomeIcon } from "@fortawesome/vue-fontawesome";
import InlineButton from "../atoms/InlineButton.vue";
import TextInput from "../atoms/Inputs/TextInput.vue";
import InputBase from "../atoms/Inputs/mixins/InputBase";
import FormMixin from "../mixins/FormMixin";

library.add(faPlus, faTimes);

/**
 * @group Inputs
 * Email Input component that allows for multiple fields.
 */
export default {
  mixins: [FormMixin, InputBase],
  components: {
    FontAwesomeIcon,
    TextInput,
    InlineButton,
  },
  name: "EmailInputList",
  data() {
    return {};
  },

  props: {
    /**
     * @vuese
     * The maximum amount of email list items allowed.
     */
    maxSize: {
      type: Number,
      default: 5,
    },
    inputLabel: {
      type: String,
      default: "Email",
    },
    enableExternalValidation: {
      type: Boolean,
      default: false,
    },
    // Array of boolean values that serves as a map to the input/v-model array for which array item has a validationError.
    // E.g. when externally validating email, if the value passed is ['mail@example.com', 'test.feedbackcompany.com'],
    // then the expected externalValidationError array content is [false, true].
    externalValidationErrors: {
      type: Array,
      default() {
        return [];
      },
    },
    // Array of string values that serves as a map to the input/v-model Array for which Array item has which validationErrorMessage
    // E.g. when externally validating email, the value passed is ['mail@example.com', 'test.feedbackcompany.com'],
    // then the array may look like this: ['', 'Incorrect email'].
    externalValidationErrorMessages: {
      type: Array,
      default() {
        return [];
      },
    },

    addAnotherLabel: {
      type: String,
      default: "Add another",
    },
  },
  computed: {
    isMobile() {
      return isMobile({ tablet: true });
    },
  },
  methods: {
    remove(emailIndex) {
      if (this.disabled) {
        return;
      }

      if (this.inputValue.length === 1) {
        this.inputValue = [""];
      } else {
        this.inputValue = this.inputValue.filter(
          (item, index) => index !== Number(emailIndex)
        );
      }
    },

    add(e) {
      e.preventDefault();
      this.inputValue.push("");
    },
  },

  mounted() {
    if (this.inputValue.length === 0) {
      this.inputValue = [""];
    }
  },
};
</script>

<style scoped lang="scss">
@import "../../style_variables/style_variables.scss";

.email_input__wrapper {
  width: 100%;
}

.email_input__item {
  display: flex;
  margin-bottom: 16px;
  position: relative;

  button {
    @media (min-width: 530px) {
      position: absolute;
      left: 100%;
      top: 0;
    }
  }
}

.add_more {
  @extend %button_typography_style;
  border: none;
  background-color: $white;
  cursor: pointer;
  padding: 0;
  color: $blue;

  &:hover {
    color: $blue_sapphire;
  }

  span {
    padding-right: 8px;
  }
}
</style>
